import {css, useMediaQuery, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LightSplitLayoutProps,
  LoadingSpinner,
  mixins,
  omitNullsy,
  useLocationQuery,
  useXlOrLarger,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  DEFAULT_PAGE_SIZE,
  Formatters,
  JustTooltipFormatter,
  MaterialTableWrapper,
  WallabyCoreSchema,
  getDefaultMTOptions,
  useFormatters,
  useTableData,
  fileContainer,
  LightDateRangeFilter,
  LightTextFilter,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  useTableParams,
  clampPageNumber,
  useOrganizations,
  LightFilterPanel,
  useUserFilters,
  UserFilterDto,
  TableSorting,
  TablePaging,
  LightOrganizationFilter2,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo, useRef} from 'react';
import {Helmet} from 'react-helmet';

function usePatientsLayoutSplitConfig(): LightSplitLayoutProps[] {
  let initSizes = [16, 100];

  const smallerThan1280 = useMediaQuery('(max-width:1280px)', {noSsr: true});
  const smallerThan1440 = useMediaQuery('(max-width:1440px)', {noSsr: true});
  if (smallerThan1280) {
    initSizes = [37, 79];
  } else if (smallerThan1440) {
    initSizes = [29, 87];
  }

  return [
    {
      initSizes,
      minSizes: [64, 212],
      storageId: '/LightSplitLayout.PatientListLayout.A',
    },
  ];
}

interface OwnProps {
  config: ConfigObjectType;
  route: string;
}
type Props = OwnProps;

type RowType = WallabyCoreSchema.components['schemas']['PatientDto'];
const EMPTY = '-';
function useColumns(formatters: Formatters, defaultSort: TableSorting): Column<RowType>[] {
  const {t, formatDate} = formatters;
  const xlOrLarger = useXlOrLarger();
  const columns: Column<RowType>[] = [
    {
      field: 'fullName',
      title: t('patient.list.grid.fullName'),
      tooltip: t('patient.list.grid.fullName.tooltip'),
      render: (data) => (
        <fileContainer.EntityLink
          route={`/patients/${data.id}`}
          titleText={data?.fullName}
          searchParams={{}}
          suppressPadding
          textAlign='left'
        >
          {data?.fullName}
        </fileContainer.EntityLink>
      ),
      width: '300px',
      sorting: true,
    },
    {
      field: 'identificationNumber',
      title: t('patient.list.grid.identificationNumber'),
      tooltip: t('patient.list.grid.identificationNumber.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.identificationNumber || EMPTY}
          tooltipOverride={data?.identificationNumber}
        />
      ),
      width: xlOrLarger ? '190px' : '',
      sorting: true,
    },
    {
      field: 'birthDate',
      title: t('patient.list.grid.birthDate'),
      tooltip: t('patient.list.grid.birthDate.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.birthDate ? formatDate(data?.birthDate ?? '') : EMPTY}
          tooltipOverride={data?.birthDate && formatDate(data?.birthDate ?? '')}
        />
      ),
      width: xlOrLarger ? '174px' : '',
      sorting: true,
    },
    {
      field: 'genderCode',
      title: t('patient.list.grid.genderCode'),
      tooltip: t('patient.list.grid.genderCode.tooltip'),
      render: (data) => (
        <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.genderCode || EMPTY} tooltipOverride={data?.genderCode} />
      ),
      width: xlOrLarger ? '122px' : '',
      sorting: true,
    },
    {
      field: 'lastHospitalizationStartDate',
      title: t('patient.list.grid.lastHospitalizationStartDate'),
      tooltip: t('patient.list.grid.lastHospitalizationStartDate.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.lastHospitalizationStartDate ? formatDate(data?.lastHospitalizationStartDate) : EMPTY}
          tooltipOverride={data?.lastHospitalizationStartDate && formatDate(data?.lastHospitalizationStartDate)}
        />
      ),
      width: '',
    },
    {
      field: 'lastHospitalizationEndDate',
      title: t('patient.list.grid.lastHospitalizationEndDate'),
      tooltip: t('patient.list.grid.lastHospitalizationEndDate.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.lastHospitalizationEndDate ? formatDate(data?.lastHospitalizationEndDate) : EMPTY}
          tooltipOverride={data?.lastHospitalizationEndDate && formatDate(data?.lastHospitalizationEndDate)}
        />
      ),
      width: '',
    },
    {
      field: 'lastHospitalizationOrganization',
      title: t('patient.list.grid.lastHospitalizationOrganization'),
      tooltip: t('patient.list.grid.lastHospitalizationOrganization.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.lastHospitalizationOrganization ?? EMPTY}
          tooltipOverride={data?.lastHospitalizationOrganization}
        />
      ),
      width: '',
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: () => 0,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

type PatientListFilters = {
  /* TODO */
  fullName: string;
  identificationNumber: string;
  srcId: string;
  organizationIds: string[];
  encounterDate: {startDate: string; endDate: string; floatingShortcutName: ShortcutEnum | null} | undefined | null;
};
type FiltersData = {
  patientsOpen: boolean;
  patients: PatientListFilters;
  patientsSorting: TableSorting;
  patientsPaging: TablePaging;
};
const DEFAULT_PATIENTS_FILTERS_OPEN = true;
const DEFAULT_PATIENTS_FILTERS = {};
const DEFAULT_PATIENTS_SORTING = {};
const DEFAULT_PATIENTS_PAGING = {};
export function normalizePatientListFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.encounterDate?.floatingShortcutName) {
    delete (copy.encounterDate as any).startDate;
    delete (copy.encounterDate as any).endDate;
    if (Object.keys(copy.encounterDate ?? {}).length === 0) {
      copy.encounterDate = null;
    }
  }
  return copy;
}
const PatientListLayout: React.FC<Props> = (props) => {
  const {config, route: _} = props;
  const storageIdPrefix = '/patients';
  const component = 'patientList';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      patientsOpen: query?.patientsOpen ?? DEFAULT_PATIENTS_FILTERS_OPEN,
      patients: query?.patients ?? DEFAULT_PATIENTS_FILTERS,
      patientsSorting: query?.patientsSorting ?? DEFAULT_PATIENTS_SORTING,
      patientsPaging: query?.patientsPaging ?? DEFAULT_PATIENTS_PAGING,
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({patients: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <PatientListLayoutInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
PatientListLayout.displayName = 'PatientListLayout';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
const PatientListLayoutInner: React.FC<InnerProps> = (props) => {
  const {
    config,
    route: _,
    storageIdPrefix,
    component,
    userFiltersLoading,
    addedUserFilters,
    userFilters,
    changeUserFilter,
    filters,
    changeFilters,
  } = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  // get data
  const patientListUrl = config.service.url.patients;
  const patientListParams = useTableParams(
    {
      fullName: filters.patients.fullName,
      identificationNumber: filters.patients.identificationNumber,
      srcId: filters.patients.srcId,
      organizationIds: filters.patients.organizationIds,
      encounterDateFrom: filters.patients.encounterDate?.startDate,
      encounterDateTo: filters.patients.encounterDate?.endDate,
      encounterFloatingShortcutName: filters.patients.encounterDate?.floatingShortcutName,
      page: filters.patientsPaging.page ?? 0,
      size: filters.patientsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    },
    filters.patientsSorting,
    {
      fullName: ['lastName', 'firstName'],
    }
  );
  const {data, isLoading, page, countOnPage, totalCount, totalPages} = useTableData<any, RowType[]>(patientListUrl, patientListParams);

  const tableFiltersForSelectedPane = useMemo(() => {
    const hasEncounterDate = !!filters.patients.encounterDate?.startDate && !!filters.patients.encounterDate.endDate;

    const result: any = {
      ...filters.patients,
    };

    if (hasEncounterDate) {
      result.encounterDate = {
        startDate: filters.patients.encounterDate?.startDate,
        endDate: filters.patients.encounterDate?.endDate,
        shortcut: filters.patients.encounterDate?.floatingShortcutName,
      };
    } else {
      delete result.encounterDate;
    }

    return result;
  }, [filters.patients]);

  const formatters = useFormatters();
  const {t, formatDate} = formatters;
  const columns = useColumns(formatters, filters.patientsSorting);
  const hasFilters = !isEqual(omitNullsy(filters.patients), omitNullsy(DEFAULT_PATIENTS_FILTERS));
  const hasSorting = !isEqual(omitNullsy(filters.patientsSorting), omitNullsy(DEFAULT_PATIENTS_SORTING));
  const {data: organizations} = useOrganizations(config);
  const patientsLayoutSplitConfig = usePatientsLayoutSplitConfig();
  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  const selectedFiltersPane =
    Object.keys(omitNullsy(tableFiltersForSelectedPane)).length > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={[
          {
            name: 'fullName',
            label: t('filter.patient.fullName'),
            value: filters.patients.fullName,
          },
          {
            name: 'identificationNumber',
            label: t('filter.patient.identificationNumber'),
            value: String(filters.patients.identificationNumber),
          },
          {
            name: 'encounterDate',
            label: t('filter.patient.encounterDate'),
            value: `${formatDate(filters.patients.encounterDate?.startDate ?? '')} - ${formatDate(
              filters.patients.encounterDate?.endDate ?? ''
            )}`,
          },
          {
            name: 'organizationIds',
            label: t('filter.organizationIds'),
            value: (filters.patients.organizationIds ?? [])
              .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
              .join(', '),
          },
          {
            name: 'srcId',
            label: t('filter.patient.srcId'),
            value: filters.patients.srcId,
          },
        ]}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={(newTableFilters) =>
          changeFilters({
            patients: newTableFilters,
            patientsPaging: {
              ...filters.patientsPaging,
              page: 0,
            },
          })
        }
        formatters={formatters}
      />
    ) : null;
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={t('patient.list.pageTitle')} />
      <LightSplitLayout
        initSizes={patientsLayoutSplitConfig[0].initSizes}
        minSizes={patientsLayoutSplitConfig[0].minSizes}
        storageId={patientsLayoutSplitConfig[0].storageId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.patientsOpen, false]}
        hidden={[false, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.patientsOpen}}
      >
        <div css={muiStyles.splitContentBox}>
          <LightFilterPanel
            open={filters.patientsOpen}
            toggleOpen={() =>
              changeFilters({
                patientsOpen: !filters.patientsOpen,
              })
            }
            hasActiveFilter={hasFilters}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: filters.patients,
              setFilter: (newFilter) => {
                changeFilters({
                  patients: newFilter,
                });
              },
              config,
              normalizeForCompare: normalizePatientListFiltersForCompare,
            }}
            selectedFilters={selectedFiltersPane}
          >
            <LightTextFilter
              config={{
                name: 'fullName',
                ui: {label: 'filter.patient.fullName'},
              }}
              value={filters.patients.fullName}
              onChange={(newValue) =>
                changeFilters({
                  patients: {
                    ...filters.patients,
                    fullName: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightTextFilter
              config={{
                name: 'identificationNumber',
                ui: {label: 'filter.patient.identificationNumber'},
              }}
              value={filters.patients.identificationNumber}
              onChange={(newValue) =>
                changeFilters({
                  patients: {
                    ...filters.patients,
                    identificationNumber: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightDateRangeFilter
              config={{
                name: 'encounterDate',
                ui: {label: 'filter.patient.encounterDate'},
              }}
              value={
                filters.patients.encounterDate?.startDate && filters.patients.encounterDate?.endDate
                  ? {
                      startDate: filters.patients.encounterDate.startDate,
                      endDate: filters.patients.encounterDate.endDate,
                      shortcut: filters.patients.encounterDate.floatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    encounterDate:
                      newValue && Object.keys(newValue).length > 0
                        ? {
                            startDate: newValue?.startDate,
                            endDate: newValue?.endDate,
                            floatingShortcutName: newValue?.shortcut,
                          }
                        : undefined,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightOrganizationFilter2
              appConfig={config}
              config={{
                name: 'organizationIds',
                ui: {label: 'filter.organizationIds'},
              }}
              multi
              value={filters.patients.organizationIds}
              onChange={(newOrganizationIds) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    organizationIds: newOrganizationIds,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightTextFilter
              config={{
                name: 'srcId',
                ui: {label: 'filter.patient.srcId'},
              }}
              value={filters.patients.srcId}
              onChange={(newValue) =>
                changeFilters({
                  patients: {
                    ...filters.patients,
                    srcId: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                })
              }
            />
          </LightFilterPanel>
        </div>
        <div data-name='patient-list-wrapper' css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'patient.list.pageTitle'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightSelectedFilters
              hasSorting={hasSorting}
              handleResetSorting={() =>
                changeFilters({
                  patientsSorting: DEFAULT_PATIENTS_SORTING,
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                })
              }
              hasFilters={hasFilters}
              handleResetFilters={() =>
                changeFilters({
                  patients: DEFAULT_PATIENTS_FILTERS,
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                })
              }
            />
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            options={{
              ...getDefaultMTOptions(countOnPage ?? DEFAULT_PAGE_SIZE),
            }}
            variant='big'
            useMaxHeight
            columns={columns}
            data={data ?? []}
            isLoading={isLoading}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onChangePage={(page, pageSize) =>
              changeFilters({
                patientsPaging: {
                  ...filters.patientsPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                patientsPaging: {
                  ...filters.patientsPaging,
                  pageSize,
                  page: clampPageNumber(filters.patientsPaging.page, pageSize, totalCount),
                },
              })
            }
            controlledPageSize={filters.patientsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
            onOrderChange={(colIndex, direction) => {
              const columnId = columns[colIndex].field as string;
              changeFilters({
                patientsSorting: {
                  field: columnId,
                  direction,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            controlledSort={(() => {
              return filters.patientsSorting;
            })()}
            scrollStorageId='/patients.scrollXY'
          />
        </div>
      </LightSplitLayout>
    </div>
  );
};

export default PatientListLayout;

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
